import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export default function ProjectDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [related, setRelated] = useState([]);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    api.get(`/projects/${slug}`).then((r) => setP(r.data)).catch(() => setP(false));
    api.get(`/projects/${slug}/related`).then((r) => setRelated(r.data)).catch(() => {});
  }, [slug]);

  if (p === null) return <div className="container-editorial py-24">Loading…</div>;
  if (!p) return <div className="container-editorial py-24"><h1 className="font-serif text-4xl">Project not found</h1></div>;

  const gallery = p.gallery || [];
  const openLB = (i) => setLightbox(i);
  const closeLB = () => setLightbox(null);
  const nextLB = () => setLightbox((i) => (i + 1) % gallery.length);
  const prevLB = () => setLightbox((i) => (i - 1 + gallery.length) % gallery.length);

  return (
    <div data-testid="page-project-detail" className="bg-[#F9F8F6]">
      <section className="relative">
        <div className="aspect-[16/9] md:aspect-[21/9] w-full overflow-hidden">
          <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover" />
        </div>
      </section>
      <section className="container-editorial py-14 md:py-20 grid grid-cols-12 gap-10 overflow-hidden">
        <div className="col-span-12 md:col-span-8">
          <div className="text-[10px] uppercase tracking-[0.32em] text-[#7A736C] mb-6"><Link to="/portfolio" className="hover:text-[#A85C42]">Projects</Link> · {p.category}</div>
          <h1 className="font-serif text-5xl md:text-7xl tracking-tight leading-[1]">{p.title}</h1>
          <p className="mt-6 text-lg text-[#4A4642] max-w-2xl leading-relaxed">{p.excerpt}</p>
        </div>
        <div className="col-span-12 md:col-span-4 md:pt-8">
          <dl className="grid grid-cols-2 md:grid-cols-1 gap-6 text-sm">
            <div><dt className="text-[10px] uppercase tracking-[0.28em] text-[#7A736C]">Location</dt><dd className="mt-1">{p.location || "—"}</dd></div>
            <div><dt className="text-[10px] uppercase tracking-[0.28em] text-[#7A736C]">Year</dt><dd className="mt-1">{p.year || "—"}</dd></div>
            <div><dt className="text-[10px] uppercase tracking-[0.28em] text-[#7A736C]">Style</dt><dd className="mt-1">{p.style || "—"}</dd></div>
            <div><dt className="text-[10px] uppercase tracking-[0.28em] text-[#7A736C]">Services</dt><dd className="mt-1">{(p.services || []).join(", ") || "—"}</dd></div>
          </dl>
        </div>
      </section>

      <section className="container-editorial pb-16 grid grid-cols-12 gap-10 overflow-hidden">
        {p.client_brief && <div className="col-span-12 md:col-span-6"><div className="text-[10px] uppercase tracking-[0.28em] text-[#A85C42] mb-3">Client Brief</div><p className="text-[#4A4642] leading-relaxed">{p.client_brief}</p></div>}
        {p.concept && <div className="col-span-12 md:col-span-6"><div className="text-[10px] uppercase tracking-[0.28em] text-[#A85C42] mb-3">Design Concept</div><p className="text-[#4A4642] leading-relaxed">{p.concept}</p></div>}
        {p.challenges && <div className="col-span-12 md:col-span-6"><div className="text-[10px] uppercase tracking-[0.28em] text-[#A85C42] mb-3">Challenges</div><p className="text-[#4A4642] leading-relaxed">{p.challenges}</p></div>}
        {p.materials && <div className="col-span-12 md:col-span-6"><div className="text-[10px] uppercase tracking-[0.28em] text-[#A85C42] mb-3">Materials & Palette</div><p className="text-[#4A4642] leading-relaxed">{p.materials}</p></div>}
      </section>

      {gallery.length > 0 && (
        <section className="container-editorial pb-16">
          <div className="grid grid-cols-12 gap-4">
            {gallery.map((g, i) => {
              // Alternate between wide feature and half card, but always fill a row
              const isFeature = i % 3 === 0;
              const layout = isFeature ? "col-span-12 md:col-span-8 aspect-[16/10]" : "col-span-6 md:col-span-4 aspect-[4/5]";
              return (
                <button key={i} onClick={() => openLB(i)} data-testid={`gallery-item-${i}`} className={`${layout} overflow-hidden rounded-sm group`}>
                  <img src={g.url} alt={g.alt || p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" loading="lazy" />
                </button>
              );
            })}
          </div>
        </section>
      )}

      {p.before_after && p.before_after.length > 0 && (
        <section className="bg-[#EAE6DF] py-20 relative noise" data-testid="before-after">
          <div className="container-editorial">
            <div className="text-[11px] uppercase tracking-[0.32em] text-[#A85C42] mb-4">Before & After</div>
            <h2 className="font-serif text-3xl md:text-5xl tracking-tight mb-10">The transformation.</h2>
            <div className="space-y-10">
              {p.before_after.map((ba, i) => (
                <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-1 bg-[#1A1918] rounded-sm overflow-hidden">
                  <div className="relative">
                    <img src={ba.before} alt="Before" className="w-full aspect-[4/3] object-cover" />
                    <div className="absolute top-4 left-4 bg-[#1A1918]/80 text-white text-[10px] uppercase tracking-[0.28em] px-3 py-1.5">Before</div>
                  </div>
                  <div className="relative">
                    <img src={ba.after} alt="After" className="w-full aspect-[4/3] object-cover" />
                    <div className="absolute top-4 left-4 bg-[#A85C42] text-white text-[10px] uppercase tracking-[0.28em] px-3 py-1.5">After</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {p.instagram_url && (
        <section className="container-editorial pb-16">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#A85C42] mb-3">On Instagram</div>
          <a href={p.instagram_url} target="_blank" rel="noreferrer" className="text-lg underline underline-offset-4 hover:text-[#A85C42]">{p.instagram_url}</a>
        </section>
      )}

      {related.length > 0 && (
        <section className="bg-[#EAE6DF] py-20 md:py-28 relative noise">
          <div className="container-editorial">
            <h2 className="font-serif text-3xl md:text-5xl tracking-tight mb-10">Also from the studio</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((r) => (
                <Link key={r.id} to={`/portfolio/${r.slug}`} className="group">
                  <div className="aspect-[4/5] overflow-hidden rounded-sm"><img src={r.cover_image} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]" /></div>
                  <div className="mt-4 font-serif text-2xl tracking-tight">{r.title}</div>
                  <div className="text-xs uppercase tracking-[0.2em] text-[#7A736C]">{r.location}</div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {lightbox !== null && (
        <div data-testid="lightbox" className="fixed inset-0 z-50 bg-[#1A1918]/95 flex items-center justify-center p-6" onClick={closeLB}>
          <button className="absolute top-6 right-6 text-white" onClick={closeLB} data-testid="lightbox-close"><X size={28} /></button>
          <button className="absolute left-6 top-1/2 -translate-y-1/2 text-white" onClick={(e) => { e.stopPropagation(); prevLB(); }} data-testid="lightbox-prev"><ChevronLeft size={40} strokeWidth={1} /></button>
          <button className="absolute right-6 top-1/2 -translate-y-1/2 text-white" onClick={(e) => { e.stopPropagation(); nextLB(); }} data-testid="lightbox-next"><ChevronRight size={40} strokeWidth={1} /></button>
          <img src={gallery[lightbox].url} alt={gallery[lightbox].alt || p.title} className="max-w-[92vw] max-h-[85vh] object-contain" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}
